import { ReactElement, ReactNode } from 'react';
import {
  AppShell as MantineAppShell,
  AppShellStylesNames,
  CSSProperties,
} from '@mantine/core';
import clsx from 'clsx';

import styles from './appShell.module.scss';

export type AppShellProps = {
  children: ReactNode;
  navigationHeader: ReactNode;
  headerHeight?: number | string;
  navigation: ReactNode;
  navigationCollapsed: boolean;
  navigationFooter?: ReactNode;
  navigationWidthCollapsed: number;
  navigationWidthExpanded: number;
  overrideStyle?: Partial<Record<AppShellStylesNames, CSSProperties>>;
  subNavigation?: ReactElement;
};

AppShell.Header = MantineAppShell.Header;

function toHeaderHeightCssVariable(
  headerHeight: number | string | undefined
): CSSProperties | undefined {
  if (headerHeight == null) {
    return undefined;
  }

  const value =
    typeof headerHeight === 'number' ? `${headerHeight}px` : headerHeight;

  return {
    '--app-shell-header-height': value,
  } as CSSProperties;
}

export function AppShell({
  children,
  headerHeight,
  navigationHeader,
  navigation,
  navigationCollapsed,
  navigationFooter,
  navigationWidthCollapsed,
  navigationWidthExpanded,
  overrideStyle,
  subNavigation,
}: AppShellProps) {
  return (
    <MantineAppShell
      transitionDuration={0}
      layout="alt"
      padding={0}
      withBorder={false}
      classNames={{
        root: styles['root'],
        main: styles['main'],
        navbar: styles['navbar'],
      }}
      style={toHeaderHeightCssVariable(headerHeight)}
      styles={overrideStyle}
      header={
        subNavigation && headerHeight ? { height: headerHeight } : undefined
      }
      navbar={{
        width: navigationCollapsed
          ? navigationWidthCollapsed
          : navigationWidthExpanded,
        breakpoint: '',
      }}
    >
      {subNavigation}
      <MantineAppShell.Navbar unstyled>
        <MantineAppShell.Section className={clsx(styles['navbar__header'])}>
          {navigationHeader}
        </MantineAppShell.Section>
        <MantineAppShell.Section
          grow
          className={styles['navbar__main-content']}
        >
          {navigation}
        </MantineAppShell.Section>
        {navigationFooter && (
          <MantineAppShell.Section
            className={clsx(styles['navbar__footer'])}
            data-testid="navigation-footer"
          >
            {navigationFooter}
          </MantineAppShell.Section>
        )}
      </MantineAppShell.Navbar>
      <MantineAppShell.Main>{children}</MantineAppShell.Main>
    </MantineAppShell>
  );
}
